// ============================================
// GBufferTarget — MRT wrapper for 2D GBuffer
// ============================================

import type { IRenderContext } from '../core/IRenderContext';
import {
  type MRTRenderTargetHandle,
  type TextureHandle,
  type MRTRenderTargetInfo,
  type TextureFormat,
  NULL_HANDLE,
} from '../types';

/**
 * GBuffer attachment indices.
 */
export const GBuffer = {
  /** RT0: Albedo.rgb + Alpha — RGBA16F */
  ALBEDO: 0,
  /** RT1: Normal.xy (2D signed) — RGBA16F */
  NORMAL: 1,
  /** RT2: Emission.rgb + MaterialFlags — RGBA16F */
  EMISSION: 2,
} as const;

/**
 * GBuffer attachment formats.
 * All RGBA16Float for HDR precision and consistent pipeline compatibility.
 */
export const GBUFFER_FORMATS: readonly TextureFormat[] = [
  'rgba16float',   // RT0: Albedo (HDR color)
  'rgba16float',   // RT1: Normal (signed, needs float range)
  'rgba16float',   // RT2: Emission (HDR emission)
] as const;

/**
 * GBufferTarget manages the GBuffer MRT set for deferred 2D rendering.
 *
 * Layout:
 *   - RT0 (RGBA16F):  Albedo.rgb + Alpha
 *   - RT1 (RGBA16F):  Normal.xy (2D signed octahedral) + spare channels
 *   - RT2 (RGBA16F):  Emission.rgb + MaterialFlags
 *   - Depth (optional): depth24plus
 *
 * Usage:
 * ```ts
 * const gbuffer = new GBufferTarget(ctx, 1920, 1080);
 *
 * // Bind for GBuffer write pass
 * gbuffer.bind();
 * gbuffer.clear();
 * // ... draw lit sprites with GBuffer material ...
 *
 * // Read individual textures in light accumulation pass
 * ctx.bindTexture(0, gbuffer.albedoTexture);
 * ctx.bindTexture(1, gbuffer.normalTexture);
 * ctx.bindTexture(2, gbuffer.emissionTexture);
 *
 * // Resize on window change
 * gbuffer.resize(newWidth, newHeight);
 *
 * // Cleanup
 * gbuffer.destroy();
 * ```
 */
export class GBufferTarget {
  private ctx: IRenderContext;
  private handle: MRTRenderTargetHandle = NULL_HANDLE;
  private info: MRTRenderTargetInfo | null = null;
  private _width: number;
  private _height: number;
  private _hasDepth: boolean;

  /** Create a GBuffer target. */
  constructor(ctx: IRenderContext, width: number, height: number, hasDepth = false) {
    this.ctx = ctx;
    this._width = width;
    this._height = height;
    this._hasDepth = hasDepth;
    this.create();
  }

  // -----------------------------------------------------------------------
  // Accessors
  // -----------------------------------------------------------------------

  get width(): number { return this._width; }
  get height(): number { return this._height; }

  /** Albedo texture (RT0: RGBA16F — albedo.rgb + alpha). */
  get albedoTexture(): TextureHandle {
    return this.info?.colorTextures[GBuffer.ALBEDO] ?? NULL_HANDLE;
  }

  /** Normal texture (RT1: RGBA16F — normal.xy + spare). */
  get normalTexture(): TextureHandle {
    return this.info?.colorTextures[GBuffer.NORMAL] ?? NULL_HANDLE;
  }

  /** Emission texture (RT2: RGBA16F — emission.rgb + materialFlags). */
  get emissionTexture(): TextureHandle {
    return this.info?.colorTextures[GBuffer.EMISSION] ?? NULL_HANDLE;
  }

  /** Depth texture (or NULL_HANDLE if no depth). */
  get depthTexture(): TextureHandle {
    return this.info?.depthTexture ?? NULL_HANDLE;
  }

  /** Number of color attachments (always 3 for GBuffer). */
  get attachmentCount(): number {
    return this.info?.attachmentCount ?? 0;
  }

  /** MRT handle for low-level access. */
  get mrtHandle(): MRTRenderTargetHandle {
    return this.handle;
  }

  // -----------------------------------------------------------------------
  // Lifecycle
  // -----------------------------------------------------------------------

  /** Bind this GBuffer as the active render target (sets MRT). */
  bind(): void {
    this.ctx.setMRTRenderTarget(this.handle);
  }

  /** Clear all GBuffer attachments. */
  clear(r = 0, g = 0, b = 0, a = 0): void {
    this.ctx.clear(r, g, b, a);
  }

  /** Resize the GBuffer. Destroys and recreates all textures. */
  resize(width: number, height: number): void {
    if (width === this._width && height === this._height) return;
    this._width = width;
    this._height = height;
    this.destroy();
    this.create();
  }

  /** Destroy all GPU resources. */
  destroy(): void {
    if (this.handle !== NULL_HANDLE) {
      this.ctx.destroyMRTRenderTarget(this.handle);
      this.handle = NULL_HANDLE;
      this.info = null;
    }
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  private create(): void {
    this.handle = this.ctx.createMRTRenderTarget({
      width: this._width,
      height: this._height,
      colorAttachments: [
        { format: GBUFFER_FORMATS[GBuffer.ALBEDO], label: 'gbuffer-albedo' },
        { format: GBUFFER_FORMATS[GBuffer.NORMAL], label: 'gbuffer-normal' },
        { format: GBUFFER_FORMATS[GBuffer.EMISSION], label: 'gbuffer-emission' },
      ],
      hasDepth: this._hasDepth,
      label: 'gbuffer',
    });
    this.info = this.ctx.getMRTRenderTargetInfo(this.handle);
  }
}
